import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const INDUSTRIES = ["Manufacturing","Energy & Utilities","Transportation & Logistics","Technology","Retail","Construction","Agriculture","Finance","Aviation","Other"];

export default function AiAssistant() {
  const [form, setForm] = useState({ industry:"Manufacturing", employees:100, annual_revenue_usd:5000000, electricity_kwh_year:250000, fuel_liters_year:10000, travel_miles_year:80000, notes:"" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const analyze = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const payload = { ...form, employees:Number(form.employees), annual_revenue_usd:Number(form.annual_revenue_usd), electricity_kwh_year:Number(form.electricity_kwh_year), fuel_liters_year:Number(form.fuel_liters_year), travel_miles_year:Number(form.travel_miles_year) };
      const { data } = await api.post("/ai/analyze", payload);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI service failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-[#D97757]"/>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">AI Climate Advisor</div>
      </div>
      <h1 className="text-4xl sm:text-5xl tracking-tighter font-light mt-2">Estimate your emissions, get matched to projects</h1>
      <p className="text-stone-600 mt-2 max-w-3xl">Powered by Claude Sonnet 4.5. Enter approximate figures — the more accurate, the better the estimate.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <form onSubmit={analyze} className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4 h-fit">
          <div>
            <Label>Industry</Label>
            <Select value={form.industry} onValueChange={set("industry")}>
              <SelectTrigger className="mt-1 bg-stone-50 border-stone-200" data-testid="ai-industry"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-white">{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Employees</Label><Input type="number" value={form.employees} onChange={set("employees")} className="mt-1 bg-stone-50 border-stone-200" data-testid="ai-employees"/></div>
            <div><Label>Annual revenue (USD)</Label><Input type="number" value={form.annual_revenue_usd} onChange={set("annual_revenue_usd")} className="mt-1 bg-stone-50 border-stone-200"/></div>
            <div><Label>Electricity (kWh/yr)</Label><Input type="number" value={form.electricity_kwh_year} onChange={set("electricity_kwh_year")} className="mt-1 bg-stone-50 border-stone-200"/></div>
            <div><Label>Fuel (L/yr)</Label><Input type="number" value={form.fuel_liters_year} onChange={set("fuel_liters_year")} className="mt-1 bg-stone-50 border-stone-200"/></div>
            <div className="col-span-2"><Label>Business travel (miles/yr)</Label><Input type="number" value={form.travel_miles_year} onChange={set("travel_miles_year")} className="mt-1 bg-stone-50 border-stone-200"/></div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 bg-stone-50 border-stone-200" placeholder="Data centers, cold chain, heavy machinery, etc."/></div>
          <Button type="submit" disabled={loading} data-testid="ai-analyze" className="w-full h-12 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Analyzing…</> : "Analyze & recommend"}
          </Button>
        </form>

        <div className="space-y-4">
          {!result && !loading && (
            <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-10 text-center text-stone-500">
              Results will appear here after you run the analysis.
            </div>
          )}
          {loading && (
            <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-500">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2"/> Claude is estimating your footprint…
            </div>
          )}
          {result && (
            <>
              <div className="bg-[#2C5530] text-white rounded-2xl p-6">
                <div className="text-xs uppercase tracking-widest text-white/70">Estimated annual emissions</div>
                <div className="text-5xl font-light tracking-tighter mt-2" data-testid="ai-estimate">{Number(result.estimated_tons_co2).toLocaleString()} t CO₂</div>
              </div>
              <div className="bg-white border border-stone-200 rounded-2xl p-6">
                <div className="text-sm font-medium mb-3">Emission breakdown</div>
                <div className="space-y-3">
                  {(result.breakdown || []).map((b, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{b.source}</span>
                        <span className="text-stone-500">{b.tons} t · {b.share_pct}%</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#2C5530]" style={{ width: `${b.share_pct}%` }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {result.insights?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-2xl p-6">
                  <div className="text-sm font-medium mb-3">Insights</div>
                  <ul className="space-y-2 text-sm text-stone-700 list-disc pl-5">
                    {result.insights.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <div className="text-sm font-medium mb-3">Recommended projects</div>
                <div className="space-y-3">
                  {(result.recommendations || []).map((r, i) => (
                    <Link key={i} to={`/project/${r.project?.id}`} data-testid={`ai-rec-${i}`} className="flex items-center gap-4 bg-white border border-stone-200 rounded-2xl p-4 hover:-translate-y-0.5 hover:shadow-md transition-transform">
                      <img src={r.project?.image_url} alt="" className="w-16 h-16 rounded-xl object-cover"/>
                      <div className="flex-1">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-sm text-stone-600">{r.rationale}</div>
                        <div className="text-xs text-stone-500 mt-1">Suggested: {r.suggested_tons} tons · ${r.project?.price_per_ton?.toFixed(2)}/t</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
